import { SupabaseSession } from '../types/supabase';
import { supabaseAuth } from './supabaseAuth';

const FEEDBACK_FORMS_TABLE = 'user_feedback_forms';
const EIR_BACKUP_TABLE = 'eir_backups';

export interface CloudFeedbackFormRef {
  id: string;
  externalId: string;
  title?: string | null;
}

const getRestUrl = (path: string) => {
  if (!supabaseAuth.isConfigured || !import.meta.env.REACT_APP_SUPABASE_URL) {
    throw new Error('Supabase is not configured');
  }
  return `${import.meta.env.REACT_APP_SUPABASE_URL}/rest/v1/${path}`;
};

const withAuthHeaders = async (session?: SupabaseSession | null) => {
  let ensured = session ?? supabaseAuth.loadPersistedSession();
  if (!ensured) {
    ensured = await supabaseAuth.ensureSession();
  }
  if (!ensured) {
    throw new Error('No Supabase session available');
  }
  return {
    headers: {
      apikey: import.meta.env.REACT_APP_SUPABASE_ANON_KEY as string,
      Authorization: `Bearer ${ensured.accessToken}`,
      'Content-Type': 'application/json',
      Prefer: 'resolution=merge-duplicates',
    },
    session: ensured,
  };
};

const formatRefs = (forms: CloudFeedbackFormRef[]) =>
  forms.map((form) => ({
    form_id: form.id,
    external_id: form.externalId,
    title: form.title ?? null,
  }));

const encodeFilterList = (values: string[]) =>
  `(${values
    .map((value) => `"${value.replace(/"/g, '\\"')}"`)
    .join(',')})`;

export const cloudSyncService = {
  async fetchFeedbackForms(userId: string, session?: SupabaseSession | null): Promise<CloudFeedbackFormRef[]> {
    if (!supabaseAuth.isConfigured) {
      return [];
    }
    const { headers } = await withAuthHeaders(session);
    const response = await fetch(`${getRestUrl(FEEDBACK_FORMS_TABLE)}?user_id=eq.${encodeURIComponent(userId)}`, {
      method: 'GET',
      headers: {
        ...headers,
        Prefer: 'count=none',
      },
    });
    if (!response.ok) {
      throw new Error(`Failed to load feedback refs (${response.status})`);
    }
    const data = await response.json();
    return (Array.isArray(data) ? data : []).map((row) => ({
      id: row.form_id,
      externalId: row.external_id,
      title: row.title ?? null,
    }));
  },

  async storeFeedbackForms(userId: string, forms: CloudFeedbackFormRef[], session?: SupabaseSession | null): Promise<void> {
    if (!supabaseAuth.isConfigured) {
      return;
    }
    const { headers } = await withAuthHeaders(session);
    const ids = forms.map((form) => form.id);
    if (ids.length === 0) {
      await fetch(`${getRestUrl(FEEDBACK_FORMS_TABLE)}?user_id=eq.${encodeURIComponent(userId)}`, {
        method: 'DELETE',
        headers,
      });
      return;
    }

    const payload = formatRefs(forms).map((row) => ({
      ...row,
      user_id: userId,
    }));

    const response = await fetch(getRestUrl(FEEDBACK_FORMS_TABLE), {
      method: 'POST',
      headers,
      body: JSON.stringify(payload),
    });

    if (!response.ok) {
      const errorBody = await response.json().catch(() => ({}));
      throw new Error(errorBody?.message || `Failed to store feedback refs (${response.status})`);
    }

    const filter = encodeURIComponent(encodeFilterList(ids));
    await fetch(
      `${getRestUrl(FEEDBACK_FORMS_TABLE)}?user_id=eq.${encodeURIComponent(userId)}&form_id=not.in.${filter}`,
      {
        method: 'DELETE',
        headers,
      },
    );
  },

  async storeEirBackup(userId: string, yaml: string, checksum: string, session?: SupabaseSession | null): Promise<void> {
    if (!supabaseAuth.isConfigured) {
      return;
    }
    const { headers } = await withAuthHeaders(session);
    const response = await fetch(getRestUrl(EIR_BACKUP_TABLE), {
      method: 'POST',
      headers,
      body: JSON.stringify([
        {
          user_id: userId,
          content: yaml,
          checksum,
          updated_at: new Date().toISOString(),
        },
      ]),
    });
    if (!response.ok) {
      const errorBody = await response.json().catch(() => ({}));
      throw new Error(errorBody?.message || `Failed to sync EIR backup (${response.status})`);
    }
  },

  async fetchEirBackup(userId: string, session?: SupabaseSession | null): Promise<string | null> {
    if (!supabaseAuth.isConfigured) {
      return null;
    }
    try {
      const { headers } = await withAuthHeaders(session);
      const response = await fetch(`${getRestUrl(EIR_BACKUP_TABLE)}?user_id=eq.${encodeURIComponent(userId)}`, {
        method: 'GET',
        headers: {
          ...headers,
          Prefer: 'count=none',
        },
      });
      if (!response.ok) {
        if (response.status === 404) {
          return null; // No backup exists yet
        }
        throw new Error(`Failed to load EIR backup (${response.status})`);
      }
      const data = await response.json();
      if (Array.isArray(data) && data.length > 0 && data[0].content) {
        return data[0].content;
      }
      return null;
    } catch (error) {
      console.warn('[CloudSync] Failed to fetch EIR backup:', error);
      return null;
    }
  },

  async deleteEirBackup(userId: string, session?: SupabaseSession | null): Promise<void> {
    if (!supabaseAuth.isConfigured) {
      return;
    }
    try {
      const { headers } = await withAuthHeaders(session);
      const response = await fetch(`${getRestUrl(EIR_BACKUP_TABLE)}?user_id=eq.${encodeURIComponent(userId)}`, {
        method: 'DELETE',
        headers,
      });
      if (!response.ok) {
        if (response.status === 404) {
          // Backup doesn't exist, which is fine
          return;
        }
        const errorBody = await response.json().catch(() => ({}));
        throw new Error(errorBody?.message || `Failed to delete EIR backup (${response.status})`);
      }
      console.log('[CloudSync] EIR backup deleted from cloud');
    } catch (error) {
      console.warn('[CloudSync] Failed to delete EIR backup:', error);
      // Don't throw - deletion failure shouldn't block local data deletion
    }
  },
};

export { hashSHA256 as hashString } from 'utils/crypto';

